// @flow
import * as React from 'react';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Box, RadioButtonGroup } from 'grommet';
import L from 'leaflet';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import { EditorContext } from 'esm-web-pro/containers/map/editor/BaseEditor';

export const PIPE_CONNECT_KEY = 'PIPE_CONNECT_KEY';

const getChoices = (pipe, pipes) => pipes
    .filter((p) => p.uuid !== pipe.uuid
            && p.infrastructure_provider === pipe.infrastructure_provider
            && pipe.cables.length === p.cables.length
            && pipe.cables.every((cable) => p.cables.find((c) => (c.uuid === cable.uuid)))
            && !pipe.path.every((coords) => p.path.find((c) => L.latLng(coords).equals(L.latLng(c))))
            && !p.path.every((coords) => pipe.path.find((c) => L.latLng(coords).equals(L.latLng(c)))))
    .map((p) => ({ id: p.uuid, value: p.uuid, label: p.ref }));

type Props = {
    pipe: { pipe: Object, isContinuous: boolean },
    onClose: () => void,
    pipes: Array<Object>,
    continuousPipes: Array<Object>,
    onSave: () => void,
    handleHighlight: (pipe: Object, isContinuous: boolean) => {
        pointCallbacks: Array<Function>,
        pipeCoords: Array<Array<[number, number]>>,
        highlightPipeCallback: Function,
    },
    title: Object,
};

const PipeConnectSidepanel = (props: Props) => {
    const {
        pipe: { pipe, isContinuous: pipeIsContinuous }, onClose, pipes, continuousPipes, onSave, handleHighlight, title,
    } = props;

    const choices = getChoices(pipe, pipes).concat(getChoices(pipe, continuousPipes));
    const [selectedPipe, setSelectedPipe] = React.useState('');
    const editor = React.useContext(EditorContext);

    React.useEffect(() => {
        let pointCallbacks;
        let pipeCoords;
        let highlightPipeCallback;

        (async () => {
            ({ pointCallbacks, pipeCoords, highlightPipeCallback } = await handleHighlight(pipeIsContinuous, pipe));
            editor.unregisterAllLoadingCallback();
            editor.registerMultipleLoadingCallbacks([highlightPipeCallback, ...pointCallbacks]);
            editor.reloadFeatures();
        })();

        return () => {
            editor.unregisterMultipleLoadingCallbacks([...pointCallbacks, highlightPipeCallback]);
            editor.reloadFeatures();
        };
    }, []);

    React.useEffect(() => {
        let pointCallbacks;
        let pipeCoords;
        let highlightPipeCallback;

        if (selectedPipe) {
            let pipeToHighlight = pipes.find((currentPipe) => currentPipe.uuid === selectedPipe);
            let isContinuous = false;

            if (!pipeToHighlight) {
                pipeToHighlight = continuousPipes.find((currentPipe) => currentPipe.uuid === selectedPipe);
                isContinuous = true;
            }

            (async () => {
                ({
                    pointCallbacks, pipeCoords, highlightPipeCallback,
                } = await handleHighlight(isContinuous, pipeToHighlight));
                editor.registerMultipleLoadingCallbacks([highlightPipeCallback, ...pointCallbacks]);
                editor.reloadFeatures();
            })();
        }

        return () => {
            if (selectedPipe && pointCallbacks && highlightPipeCallback) {
                editor.unregisterMultipleLoadingCallbacks([...pointCallbacks, highlightPipeCallback]);
                editor.reloadFeatures();
            }
        };
    }, [selectedPipe]);

    const handleSave = () => {
        onSave(selectedPipe);
    };

    return (
        <SidePane
            id={PIPE_CONNECT_KEY}
            key={PIPE_CONNECT_KEY}
            title={title}
        >
            <Box height="100%" flex justify="between">
                <Box pad="small">
                    <RadioButtonGroup
                        name="pipe-select"
                        value={selectedPipe}
                        onChange={(event) => setSelectedPipe(event.target.value)}
                        options={choices}
                    />
                </Box>
                <ActionButtons onClose={onClose} onSave={handleSave} disabled={!selectedPipe} />
            </Box>
        </SidePane>
    );
};

export default injectIntl(PipeConnectSidepanel);
